import { accountCache } from "./account-cache";
import { homeCache, pruneHomeCache } from "./home-cache";
import { watchConnections } from "./analytics";
import { loadOrCreateIdentity, type KeyPair } from "@foreman/crypto";
import { getIdentityToken, ensureGuestIdentity } from "./identity";
import { webSocketConnector } from "@foreman/link";
import { ComputersStore, Hub, MemoryKeyValue, PowerPrefs, WebCryptoSecretStore, type KeyValue } from "@foreman/app-core";
import { openObjectStore } from "./idb";
import { WebNotifications } from "./notifications";

export interface Services {
  keys: KeyPair;
  store: ComputersStore;
  hub: Hub;
  /** False when browser storage is unavailable and this phone's identity won't survive closing the tab. */
  persistent: boolean;
  notifications: WebNotifications;
}

function localStorageKv(): KeyValue {
  try {
    const probe = "foreman.probe";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return {
      get: (k) => localStorage.getItem(k),
      set: (k, v) => localStorage.setItem(k, v),
      remove: (k) => localStorage.removeItem(k),
    };
  } catch {
    return new MemoryKeyValue();
  }
}

/** Load (or create) this phone's identity, open the computers list and start a connection to each computer. */
export async function startServices(): Promise<Services> {
  const { store: objects, persistent } = await openObjectStore();
  // Ask the browser not to evict our storage under pressure (best effort; iOS honours it for installed apps).
  void navigator.storage?.persist?.().catch(() => {});
  const keys = await loadOrCreateIdentity(new WebCryptoSecretStore(objects), "phone-identity");
  const store = new ComputersStore(localStorageKv());
  pruneHomeCache(store.list().map((c) => c.id));
  const hub = new Hub(store, { keys, connectorFor: (relayUrl, daemonKey) => webSocketConnector(relayUrl, daemonKey, { identity: getIdentityToken }), accountCache, homeCache, power: new PowerPrefs(localStorageKv()) });
  await ensureGuestIdentity(); // a first-time guest gets its relay identity before the first connection, so the relay can name its plan in the join
  hub.start();
  watchConnections(hub);
  const notifications = new WebNotifications(hub, localStorageKv());
  notifications.start();

  // A suspended page can hold a socket that looks open but is dead; on return, reconnect right away.
  // Reconnecting is cheap because output is replayed from the last offset.
  document.addEventListener("visibilitychange", () => {
    const visible = document.visibilityState === "visible";
    hub.setBackground(!visible);
    if (visible) hub.reconnectAll();
  });
  hub.setBackground(document.visibilityState === "hidden");
  window.addEventListener("online", () => hub.reconnectAll());
  window.addEventListener("pageshow", (e) => {
    if ((e as PageTransitionEvent).persisted) hub.reconnectAll();
  });
  return { keys, store, hub, persistent, notifications };
}
